import React from 'react';
import clsx from 'clsx';
import Image, { ImageProps } from 'components/commercetools-ui/atoms/image';
import useImageSizes from 'helpers/hooks/useImageSizes';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { tablet } from 'helpers/utils/screensizes';
import { BrandPromiseProps } from '../types';

type PromiseBannerProps = {
  images: [];
  className?: string;
  logo?: ImageProps;
  headlineTextSizeLg?: number;
};

const PromiseBanner: React.FC<PromiseBannerProps & BrandPromiseProps> = ({ images, className = '', ...props }) => {
  const {
    headlineTextSizeLg,
    headlineTextColor,
    headlineText,
    descriptionText,
    descriptionTextColor,
    headlineTextSizeSm,
    descriptionTextSizeLg,
    descriptionTextSizeSm,
    logo,
    brandPromiseBackgroundImageAlt,
  } = props;
  const [isDesktopSize] = useMediaQuery(tablet);
  const { brandPromiseBackgroundImageSrc, brandPromiseBackgroundImage, brandPromiseBackgroundColor } = props;
  const tileImageSizes = useImageSizes({ sm: 0.22, md: 0.15, lg: 0.1, defaultSize: 0.1, maxWidth: 160 });
  return (
    <section
      className={
        `mx-auto w-full max-w-6xl ` +
        `flex flex-col items-start gap-[20px] md:flex-row md:items-center` +
        ` pb-44 pt-24 sm:px-6 lg:px-8` +
        className
      }
      aria-label={brandPromiseBackgroundImage ? brandPromiseBackgroundImageAlt : ''}
      style={{
        background: brandPromiseBackgroundImage
          ? `url(${brandPromiseBackgroundImageSrc})`
          : brandPromiseBackgroundColor,
      }}
    >
      <div className="flex items-center" data-ffid="ctr-brand-promise-images">
        <ul className="flex" role="none">
          {images?.map(
            (
              img: {
                promiseImagesAlt: string | undefined;
                promiseImagesSrc: string | undefined;
              },
              idx: number,
            ) => (
              <li
                key={`promise-img-${img?.promiseImagesAlt ?? ''}${idx}`}
                className={clsx(
                  isDesktopSize ? 'size-156' : 'size-84',
                  idx !== 0 && '-ml-10 sm:-ml-20',
                  'aspect-1 rounded-full shadow-md ring-4 ring-white',
                )}
              >
                <Image
                  src={img.promiseImagesSrc}
                  alt={img.promiseImagesAlt}
                  data-ffid={`img-promise-image-${idx}`}
                  className="size-full rounded-full object-cover"
                  loading="lazy"
                  ratio="1:1"
                  sizes={tileImageSizes}
                />
              </li>
            ),
          )}
        </ul>
      </div>

      <div className="min-w-0 flex-1 md:py-20">
        <div style={{ width: Number(logo?.media?.width), height: Number(logo?.media?.height) }}>
          <Image
            media={logo?.media}
            width={Number(logo?.media?.width)}
            height={Number(logo?.media?.height)}
            data-ffid={`img-promise-banner-logo`}
            className="size-full object-cover"
            loading="lazy"
            alt={logo?.title}
          />
        </div>
        <h2
          className="pb-6 pt-4 font-normal leading-10 text-gray-900"
          style={{
            color: headlineTextColor,
            fontSize: `${isDesktopSize ? headlineTextSizeLg : headlineTextSizeSm}px`,
          }}
          data-ffid="lbl-promise-heading-text"
        >
          {headlineText}
        </h2>
        <p
          style={{
            color: descriptionTextColor,
            fontSize: `${isDesktopSize ? descriptionTextSizeLg : descriptionTextSizeSm}px`,
          }}
          data-ffid="lbl-promise-description-text"
        >
          {descriptionText}
        </p>
      </div>
    </section>
  );
};

export default PromiseBanner;
